import { useEffect, useMemo, useRef, useState } from 'react'
import type { TocItem } from '@/components/case-study/caseStudyDefaultToc'

type Props = {
  items: TocItem[]
  linkClassName?: string
}

const anchorToId = (anchor: string) => (anchor.startsWith('#') ? anchor.slice(1) : anchor)

/**
 * Stable scroll spy: one rAF-throttled update per frame, no IntersectionObserver
 * threshold storms (those caused visible TOC “jumping” while scrolling).
 */
const useScrollSpySectionId = (ids: string[]) => {
  const [activeId, setActiveId] = useState<string | null>(null)
  const rafRef = useRef(0)

  useEffect(() => {
    const sectionEls = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => Boolean(el))

    if (sectionEls.length === 0) {
      return
    }

    const activationLinePx = () => {
      const h = window.innerHeight
      // Band below top (progress bar + comfortable reading line); clamp for short viewports.
      return Math.min(200, Math.max(96, h * 0.28))
    }

    const update = () => {
      const line = activationLinePx()
      let current: HTMLElement | null = null
      for (const el of sectionEls) {
        if (el.getBoundingClientRect().top <= line) {
          current = el
        }
      }
      const nextId = (current ?? sectionEls[0]).id
      setActiveId((prev) => (prev === nextId ? prev : nextId))
    }

    const schedule = () => {
      if (rafRef.current !== 0) {
        return
      }
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0
        update()
      })
    }

    update()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule, { passive: true })

    return () => {
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      cancelAnimationFrame(rafRef.current)
      rafRef.current = 0
    }
  }, [ids])

  return activeId
}

export const CaseStudyContentSheet = ({ items, linkClassName = 'font-display' }: Props) => {
  const ids = useMemo(() => items.map((i) => anchorToId(i.anchor)), [items])
  const activeId = useScrollSpySectionId(ids)

  return (
    <nav className="w-full min-w-0 [contain:layout]" aria-label="On this page">
      <ul className="flex flex-col gap-1">
        {items.map((item) => {
          const id = anchorToId(item.anchor)
          const isActive = activeId === id
          return (
            <li key={item.anchor} className="flex items-start gap-2.5">
              <span
                className={`mt-[8.5px] w-5 shrink-0 rounded-full ${isActive ? 'h-0.5 bg-[#202020]' : 'h-px bg-[#e8e8e8]'}`}
                aria-hidden
              />
              <a
                href={item.anchor}
                className={`${linkClassName} text-[13px] font-normal leading-[17.88px] transition-colors duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)] ${
                  isActive
                    ? 'text-[#202020]'
                    : 'text-[#838383] hover:text-[#202020]'
                }`}
              >
                {item.label}
              </a>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
